/**
 * Contract hooks — TanStack Query mutations for Zoho Sign contract operations.
 *
 * Hooks:
 * - useSendResellerContract()          — send Zoho Sign contract to a reseller
 * - useRefreshResellerContractStatus() — poll Zoho Sign and update reseller contract status
 * - useSendFreelancerContract()          — send Zoho Sign contract to a freelancer
 * - useRefreshFreelancerContractStatus() — poll Zoho Sign and update freelancer contract status
 *
 * All mutations:
 * - Accept the partner ID as the mutation variable
 * - On success, invalidate the relevant reseller/freelancer detail cache
 *   so the detail page re-renders with the updated contract_status
 *
 * Dev mode: when ZOHO_CLIENT_ID is not set on the API server, the API returns
 * mock data — these hooks behave identically in dev and production.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiPost } from '@/lib/api'
import { resellerKeys, freelancerKeys } from './useResellers'

// ── Response types ────────────────────────────────────────────────────────────

interface SendContractResponse {
  data: {
    zohoRequestId: string
    contractStatus: string
  }
}

interface RefreshStatusResponse {
  data: {
    contractStatus: string
  }
}

// ── Reseller contract hooks ───────────────────────────────────────────────────

/**
 * Send a Zoho Sign contract to a reseller.
 *
 * The mutation variable is the reseller ID.
 * On success: invalidates the reseller detail cache so the contract status badge updates.
 *
 * @returns TanStack Query mutation with { zohoRequestId, contractStatus }
 */
export function useSendResellerContract() {
  const queryClient = useQueryClient()

  return useMutation<{ zohoRequestId: string; contractStatus: string }, Error, string>({
    mutationFn: async (resellerId: string) => {
      const res = await apiPost<SendContractResponse>(
        `/api/v1/contracts/resellers/${resellerId}/send`,
        {},
      )
      return res.data
    },
    onSuccess: (_data, resellerId) => {
      // Invalidate detail so contract_status badge refreshes immediately
      queryClient.invalidateQueries({ queryKey: resellerKeys.detail(resellerId) })
      queryClient.invalidateQueries({ queryKey: resellerKeys.all })
    },
  })
}

/**
 * Refresh the contract status for a reseller by polling Zoho Sign.
 *
 * The mutation variable is the reseller ID.
 * On success: invalidates the reseller detail cache so the contract status badge updates.
 *
 * @returns TanStack Query mutation with { contractStatus }
 */
export function useRefreshResellerContractStatus() {
  const queryClient = useQueryClient()

  return useMutation<{ contractStatus: string }, Error, string>({
    mutationFn: async (resellerId: string) => {
      const res = await apiPost<RefreshStatusResponse>(
        `/api/v1/contracts/resellers/${resellerId}/refresh-status`,
        {},
      )
      return res.data
    },
    onSuccess: (_data, resellerId) => {
      queryClient.invalidateQueries({ queryKey: resellerKeys.detail(resellerId) })
      queryClient.invalidateQueries({ queryKey: resellerKeys.all })
    },
  })
}

// ── Freelancer contract hooks ─────────────────────────────────────────────────

/**
 * Send a Zoho Sign contract to a freelancer.
 *
 * The mutation variable is the freelancer ID.
 * On success: invalidates the freelancer detail cache so the contract status badge updates.
 *
 * @returns TanStack Query mutation with { zohoRequestId, contractStatus }
 */
export function useSendFreelancerContract() {
  const queryClient = useQueryClient()

  return useMutation<{ zohoRequestId: string; contractStatus: string }, Error, string>({
    mutationFn: async (freelancerId: string) => {
      const res = await apiPost<SendContractResponse>(
        `/api/v1/contracts/freelancers/${freelancerId}/send`,
        {},
      )
      return res.data
    },
    onSuccess: (_data, freelancerId) => {
      queryClient.invalidateQueries({ queryKey: freelancerKeys.detail(freelancerId) })
      queryClient.invalidateQueries({ queryKey: freelancerKeys.all })
    },
  })
}

/**
 * Refresh the contract status for a freelancer by polling Zoho Sign.
 *
 * The mutation variable is the freelancer ID.
 * On success: invalidates the freelancer detail cache so the contract status badge updates.
 *
 * @returns TanStack Query mutation with { contractStatus }
 */
export function useRefreshFreelancerContractStatus() {
  const queryClient = useQueryClient()

  return useMutation<{ contractStatus: string }, Error, string>({
    mutationFn: async (freelancerId: string) => {
      const res = await apiPost<RefreshStatusResponse>(
        `/api/v1/contracts/freelancers/${freelancerId}/refresh-status`,
        {},
      )
      return res.data
    },
    onSuccess: (_data, freelancerId) => {
      queryClient.invalidateQueries({ queryKey: freelancerKeys.detail(freelancerId) })
      queryClient.invalidateQueries({ queryKey: freelancerKeys.all })
    },
  })
}
